import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import SignatureCanvas from '../../components/signatureCanvas';
import './style.scss';

// Opciones de configuración y datos iniciales (iguales a las del PHP original)
const ENFOQUES_DISPONIBLES = [
  'Afrodescendiente',
  'Indígena',
  'ROM',
  'LGTBI',
  'Situación de calle',
  'Campesino',
  'Discapacidad',
  'Población Víctima'
];

const EDADES = ['0-5', '6-17', '18-28', '29-54', '55+'];

const COMUNAS = [
  '1 - Popular',
  '2 - Santa Cruz',
  '3 - Manrique',
  '4 - Aranjuez',
  '5 - Castilla',
  '6 - Doce de Octubre',
  '7 - Robledo',
  '8 - Villa Hermosa',
  '9 - Buenos Aires',
  '10 - La Candelaria',
  '11 - Laureles – Estadio',
  '12 - La América',
  '13 - San Javier',
  '14 - El Poblado',
  '15 - Guayabal',
  '16 - Belén',
  '50 - San Sebastián de Palmitas',
  '60 - San Cristóbal',
  '70 - Altavista',
  '80 - San Antonio de Prado',
  '90 - Santa Elena'
];

export default function ExternalAttendanceForm() {
  const [searchParams] = useSearchParams();
  const eventoId = searchParams.get('evento_id');

  // Estados del Formulario
  const [formData, setFormData] = useState({
    documento: '',
    nombres: '',
    empresa: '',
    cargo: '',
    genero: '',
    edad: '',
    enfoques: [],
    comuna: '',
    organizacion: '',
    correo: '',
    telefono: ''
  });

  // Estados Auxiliares
  const [eventoInfo, setEventoInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [registroExitoso, setRegistroExitoso] = useState(false);
  const [autocompletadoMensaje, setAutocompletadoMensaje] = useState('');

  const signatureRef = useRef(null);
  const [hasSignature, setHasSignature] = useState(false);

  // --- OBTENER INFORMACIÓN DEL EVENTO ---
  useEffect(() => {
    if (eventoId) {
      // NOTA PARA EL DESARROLLADOR: Reemplazar con la petición al backend de AdonisJS.
      // Ejemplo: axios.get(`/api/v1/meetings/${eventoId}`).then(res => setEventoInfo(res.data))
      // Por ahora usamos datos MOCK para simular
      setEventoInfo({
        titulo: 'Taller de Presupuesto Participativo',
        fecha: new Date().toLocaleDateString('es-ES'),
        hora: '14:00',
        lugar: 'Centro de Desarrollo Social (CDS)'
      });
    }
  }, [eventoId]);

  // --- AUTOCOMPLETADO POR DOCUMENTO ---
  useEffect(() => {
    if (!formData.documento) {
      setAutocompletadoMensaje('');
      return;
    }

    const delayDebounce = setTimeout(async () => {
      setLoading(true);
      try {
        // NOTA PARA EL DESARROLLADOR: Cambiar por la consulta real al backend de AdonisJS.
        // Ejemplo: const response = await axios.get(`/api/v1/meetings/buscar-asistencia?documento=${formData.documento}`);
        if (formData.documento.length >= 6) {
          // Mock de encontrar usuario registrado previamente
          const mockUserDb = {
            '123456': {
              nombres: 'María Camila Restrepo',
              empresa: 'Corporación Comuna Viva',
              cargo: 'Coordinadora Social',
              genero: 'Mujer',
              edad: '29-54',
              enfoques: ['LGTBI', 'Campesino'],
              comuna: '10 - La Candelaria',
              organizacion: 'Junta de Acción Comunal',
              correo: 'maria.restrepo@example.com',
              telefono: '3124567890'
            }
          };

          const userFound = mockUserDb[formData.documento];

          if (userFound) {
            setFormData((prev) => ({
              ...prev,
              ...userFound
            }));
            setAutocompletadoMensaje('✨ ¡Datos encontrados y autocompletados con éxito!');
            toast.success('Datos autocompletados automáticamente');
          } else {
            setAutocompletadoMensaje('');
          }
        }
      } catch (error) {
        console.error('Error al autocompletar', error);
      } finally {
        setLoading(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(delayDebounce);
  }, [formData.documento]);

  // --- MANEJADORES DEL FORMULARIO ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => {
      const enfoques = checked
        ? [...prev.enfoques, value]
        : prev.enfoques.filter((item) => item !== value);
      return { ...prev, enfoques };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar firma
    if (!hasSignature || !signatureRef.current?.hasDrawn()) {
      toast.warning('Por favor realice su firma en el recuadro.');
      return;
    }

    setSubmitting(true);

    try {
      const signatureBase64 = signatureRef.current?.toDataURL();
      if (!signatureBase64) {
        toast.warning('Por favor realice su firma en el recuadro.');
        return;
      }

      const payload = {
        ...formData,
        evento_id: eventoId ? parseInt(eventoId) : null,
        firma: signatureBase64
      };

      // NOTA PARA EL DESARROLLADOR: Vincular con tu endpoint de registro en AdonisJS
      console.log('Payload enviado para registro externo de asistencia:', payload);

      // Simulamos llamada de red
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setRegistroExitoso(true);
      toast.success('¡Registro de asistencia completado!');
    } catch (error) {
      toast.error('Ocurrió un error al guardar los datos.');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegisterAnother = () => {
    setFormData({
      documento: '',
      nombres: '',
      empresa: '',
      cargo: '',
      genero: '',
      edad: '',
      enfoques: [],
      comuna: '',
      organizacion: '',
      correo: '',
      telefono: ''
    });
    signatureRef.current?.clear();
    setHasSignature(false);
    setRegistroExitoso(false);
    setAutocompletadoMensaje('');
  };

  if (registroExitoso) {
    return (
      <main className="external-attendance-success">
        <div className="success-card">
          <div className="success-icon-wrapper">
            <span className="material-symbols-sharp">check_circle</span>
          </div>
          <h2>¡Registro Completado!</h2>
          <p className="success-message">
            Muchas gracias por su participación. Sus datos y firma digital han sido
            registrados correctamente.
          </p>

          <div className="summary-details">
            <p><strong>Asistente:</strong> {formData.nombres}</p>
            <p><strong>Documento:</strong> {formData.documento}</p>
            {eventoInfo && <p><strong>Evento:</strong> {eventoInfo.titulo}</p>}
          </div>

          <button onClick={handleRegisterAnother} className="btn-another">
            Registrar Otra Persona
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="external-attendance">
      <section className="form-container">
        <div className="form-header">
          <div className="header-icon">
            <span className="material-symbols-sharp">edit_document</span>
          </div>
          <h1>Formulario de Registro y Caracterización</h1>
          <p className="subtitle">
            Por favor, complete los campos y firme digitalmente para registrar su asistencia.
          </p>
        </div>

        {eventoInfo && (
          <div className="event-badge">
            <div className="badge-title">
              <span className="material-symbols-sharp">event_upcoming</span>
              <span>Información del Evento</span>
            </div>
            <div className="badge-grid">
              <div><strong>Evento:</strong> {eventoInfo.titulo}</div>
              <div><strong>Lugar:</strong> {eventoInfo.lugar}</div>
              <div><strong>Fecha:</strong> {eventoInfo.fecha}</div>
              <div><strong>Hora:</strong> {eventoInfo.hora}</div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="attendance-form">
          <div className="form-row">
            <div className="form-group col-6">
              <label htmlFor="documento">Número de documento *</label>
              <div className="input-with-loader">
                <input
                  type="text"
                  name="documento"
                  id="documento"
                  required
                  placeholder="Ej: 10293847"
                  value={formData.documento}
                  onChange={handleInputChange}
                  autoComplete="off"
                />
                {loading && <div className="loader-spinner"></div>}
              </div>
              {autocompletadoMensaje && (
                <span className="autocompletado-status">{autocompletadoMensaje}</span>
              )}
            </div>

            <div className="form-group col-6">
              <label htmlFor="nombres">Nombres y apellidos *</label>
              <input
                type="text"
                name="nombres"
                id="nombres"
                required
                placeholder="Nombres completos"
                value={formData.nombres}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-6">
              <label htmlFor="empresa">Empresa / Entidad / Dependencia</label>
              <input
                type="text"
                name="empresa"
                id="empresa"
                placeholder="Nombre de la entidad"
                value={formData.empresa}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group col-6">
              <label htmlFor="cargo">Cargo / Rol / Calidad con la que actúa</label>
              <input
                type="text"
                name="cargo"
                id="cargo"
                placeholder="Ej: Delegado, Coordinador, Vecino"
                value={formData.cargo}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-6">
              <label>Género *</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="genero"
                    value="Hombre"
                    checked={formData.genero === 'Hombre'}
                    onChange={handleInputChange}
                    required
                  />
                  <span>Hombre</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="genero"
                    value="Mujer"
                    checked={formData.genero === 'Mujer'}
                    onChange={handleInputChange}
                  />
                  <span>Mujer</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="genero"
                    value="Otro"
                    checked={formData.genero === 'Otro'}
                    onChange={handleInputChange}
                  />
                  <span>Otro</span>
                </label>
              </div>
            </div>

            <div className="form-group col-6">
              <label htmlFor="edad">Rango de edad *</label>
              <select
                name="edad"
                id="edad"
                required
                value={formData.edad}
                onChange={handleInputChange}
              >
                <option value="">Seleccione rango...</option>
                {EDADES.map((rango) => (
                  <option key={rango} value={rango}>
                    {rango} {rango === '55+' ? 'o más años' : 'años'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group full-width">
            <label className="section-label">Enfoque diferencial (Seleccione todas las que apliquen)</label>
            <div className="checkbox-grid">
              {ENFOQUES_DISPONIBLES.map((enfoque) => (
                <label key={enfoque} className="checkbox-label">
                  <input
                    type="checkbox"
                    value={enfoque}
                    checked={formData.enfoques.includes(enfoque)}
                    onChange={handleCheckboxChange}
                  />
                  <span>{enfoque}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-4">
              <label htmlFor="comuna">Comuna donde reside *</label>
              <select
                name="comuna"
                id="comuna"
                required
                value={formData.comuna}
                onChange={handleInputChange}
              >
                <option value="">Seleccione comuna...</option>
                {COMUNAS.map((comuna) => (
                  <option key={comuna} value={comuna}>
                    {comuna}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group col-4">
              <label htmlFor="organizacion">Organización o grupo al que pertenece</label>
              <input
                type="text"
                name="organizacion"
                id="organizacion"
                placeholder="Ej: JAC, Asociación, Colectivo"
                value={formData.organizacion}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group col-4">
              <label htmlFor="telefono">Teléfono / Celular</label>
              <input
                type="tel"
                name="telefono"
                id="telefono"
                placeholder="Número celular"
                value={formData.telefono}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-12">
              <label htmlFor="correo">Correo electrónico *</label>
              <input
                type="email"
                name="correo"
                id="correo"
                required
                placeholder="correo@ejemplo.com"
                value={formData.correo}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-group full-width signature-group">
            <label>Firma digital obligatoria *</label>
            <SignatureCanvas
              ref={signatureRef}
              onDrawChange={setHasSignature}
            />
          </div>

          <div className="privacy-clause">
            <p>
              <strong>AUTORIZACIÓN LEY 1581 DE 2012:</strong> Los datos personales recolectados en este formulario
              son tratados de forma confidencial con fines exclusivos de control de asistencia, seguimiento y estadísticas.
              Usted tiene derecho a conocer, actualizar y rectificar sus datos personales conforme a la ley.
            </p>
          </div>

          <div className="form-actions">
            <button type="submit" disabled={submitting} className="btn-submit">
              {submitting ? 'Registrando...' : 'Confirmar Registro de Asistencia'}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}
